'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useState } from 'react';

import { loadAsset } from '../helpers/asset-helper';
import { IconBack24 } from '../icons/ic-back';
import { IconForth24 } from '../icons/ic-forth';

import ImageGallery from './ImageGallery';
import ImageWithTransition from './ImageWithTransition';

const filmEventPoster = loadAsset('/images/placeholders/landscape/im-posterFilmLandscape.jpg');

export type ImageGalleryContentProps = {
    images: string[];
    currentIndex?: number;
    arrowsOverlaying?: boolean;
    roundedCorners?: boolean;
};

function ImageGalleryContent(props: ImageGalleryContentProps): React.JSX.Element {
    const images = props.images || [];
    const [currentImageIndex, setCurrentImageIndex] = useState(props.currentIndex ? props.currentIndex : 0);
    const [fullScreenOpen, setFullScreenOpen] = useState(false);

    const t = useTranslations();

    const scrollToImage = useCallback(() => {
        if (window && typeof window !== 'undefined') {
            const selectedImage = window.document.getElementById('imageGallery__selectedImage');
            selectedImage?.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
                inline: 'center'
            });
        }
    }, []);

    useEffect(() => {
        scrollToImage();
        return;
    }, [currentImageIndex, scrollToImage]);

    useEffect(() => {
        setTimeout(() => {
            scrollToImage();
        }, 250);
    }, [images, scrollToImage]);

    const showPreviousImage = useCallback(() => {
        if (currentImageIndex > 0) {
            setCurrentImageIndex(currentImageIndex - 1);
        }
    }, [currentImageIndex]);

    const showNextImage = useCallback(() => {
        if (currentImageIndex < images.length - 1) {
            setCurrentImageIndex(currentImageIndex + 1);
        }
    }, [currentImageIndex, images.length]);

    const leftArrowKeyPress = useCallback(
        (event) => {
            if (event.keyCode === 37) {
                showPreviousImage();
            }
        },
        [showPreviousImage]
    );
    const rightArrowKeyPress = useCallback(
        (event) => {
            if (event.keyCode === 39) {
                showNextImage();
            }
        },
        [showNextImage]
    );

    useEffect(() => {
        document.addEventListener('keydown', leftArrowKeyPress);
        document.addEventListener('keydown', rightArrowKeyPress);
        return () => {
            document.removeEventListener('keydown', leftArrowKeyPress);
            document.removeEventListener('keydown', rightArrowKeyPress);
        };
    }, [leftArrowKeyPress, rightArrowKeyPress]);

    return (
        <div className="relative w-full h-full flex flex-col md:rounded-8">
            <div className="relative w-full h-0 flex-auto flex flex-row justify-center items-center">
                {images.length > 1 && (
                    <IconBack24
                        className={classnames(
                            'text-[var(--on-surface-color)] h-100 w-28 mr-8 clickable-element z-[10]',
                            {
                                'absolute left-0': props.arrowsOverlaying === true
                            }
                        )}
                        onClick={showPreviousImage}
                    />
                )}
                {images.length > currentImageIndex ? (
                    <ImageWithTransition
                        className={classnames(
                            'relative w-full max-h-full object-contain object-center cursor-pointer',
                            {
                                'rounded-[4px]': props.roundedCorners === true
                            }
                        )}
                        src={images[currentImageIndex] || filmEventPoster}
                        width={2000}
                        height={1000}
                        alt=""
                        unoptimized
                        onClick={() => setFullScreenOpen(true)}
                    />
                ) : (
                    <span className="text-[var(--on-surface-color)]">{t('common.no-images-available')}</span>
                )}
                {images.length > 1 && (
                    <IconForth24
                        className={classnames(
                            'text-[var(--on-surface-color)] h-100 w-28 ml-8 clickable-element z-[10]',
                            {
                                'absolute right-0': props.arrowsOverlaying === true
                            }
                        )}
                        onClick={showNextImage}
                    />
                )}
            </div>
            {images.length > 1 && (
                <div className="flex-none flex flex-row w-full">
                    <div
                        id="imageGallery__imagesScroller"
                        className="flex flex-row flex-auto w-0 h-auto space-x-12 px-12 py-8 overflow-auto group ">
                        {images.map((image, index) => {
                            return (
                                <div
                                    key={`GalleryItem#${image}`}
                                    className="relative flex-none w-auto h-[81px] duration-200 active:opacity-80 cursor-pointer"
                                    onClick={() => {
                                        setCurrentImageIndex(index);
                                    }}>
                                    <ImageWithTransition
                                        id={index === currentImageIndex ? 'imageGallery__selectedImage' : ''}
                                        className={classnames(
                                            'w-auto h-full',
                                            'duration-200 transform hover:scale-105',
                                            'object-contain object-center',
                                            index === currentImageIndex
                                                ? 'scale-105'
                                                : 'group-hover:opacity-100 opacity-50',
                                            {
                                                'rounded-[4px]': props.roundedCorners === true
                                            }
                                        )}
                                        src={image || filmEventPoster}
                                        width={300}
                                        height={200}
                                        blurDataURL={image || filmEventPoster}
                                        placeholder="blur"
                                        alt=""
                                        unoptimized
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {fullScreenOpen && (
                <ImageGallery
                    images={images}
                    open={fullScreenOpen}
                    currentIndex={currentImageIndex}
                    onClose={() => setFullScreenOpen(false)}
                />
            )}
        </div>
    );
}

export default ImageGalleryContent;
